<template>
    <v-card flat>
        <v-overlay :value="loadingClientData">
            <v-progress-circular indeterminate size="64"></v-progress-circular>
        </v-overlay>
        <v-toolbar color="bal-gray" flat>
            <div class="toolbar-titles">
                <v-toolbar-title>{{ activeClient.name }}</v-toolbar-title>
                <div class="toolbar-subtitle" v-if="activeClient.shop_name">{{ activeClient.shop_name }}</div>
            </div>
            <v-spacer></v-spacer>
            <v-btn
                dark
                color="bal-red"
                @click="editUser"
                v-if="hasPermissionForAction(PermissionsConstants.CLIENT, PermissionsConstants.EDIT)"
            >
                <v-icon>edit</v-icon>
                {{ "Edit" | t }}
            </v-btn>
            <v-menu
                offset-y
                bottom
                left
                v-if="hasPermissionForAction(PermissionsConstants.CLIENT, PermissionsConstants.DELETE)"
            >
                <template v-slot:activator="{ on }">
                    <v-btn color="bal-dark-gray" class="px-2 ml-2 mx-2" style="min-width: 0;" dark v-on="on">
                        <v-icon>expand_more</v-icon>
                    </v-btn>
                </template>
                <v-list>
                    <v-list-item @click="showMergeModal = true">
                        <v-list-item-title>{{ "Merge" | t }}</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="handleDeleteClick">
                        <v-list-item-title style="color: red">{{ "Delete" | t }}</v-list-item-title>
                    </v-list-item>
                </v-list>
            </v-menu>
            <v-btn @click="closeDrawer()" icon>
                <v-icon>close</v-icon>
            </v-btn>
        </v-toolbar>

        <!-- Merge Modal -->
        <v-dialog v-model="showMergeModal" max-width="500">
            <v-card>
                <v-card-title>{{ "Merge client" | t }}</v-card-title>
                <v-card-text>
                    <p class="mb-4">{{ "Merge this client into another client. All codes, rules and campaigns will be moved to the selected client." | t }}</p>
                    <v-autocomplete
                        v-model="selectedParentClient"
                        :items="availableClients"
                        :loading="loadingClients"
                        :search-input.sync="clientSearch"
                        item-text="name"
                        item-value="id"
                        :label="'Select parent client' | t"
                        :placeholder="'Start typing to Search' | t"
                        return-object
                        outlined
                        dense
                        hide-no-data
                    >
                        <template v-slot:item="{ item }">
                            <v-list-item-content>
                                <v-list-item-title>{{ item.name }}</v-list-item-title>
                                <v-list-item-subtitle>{{ item.chain_name }} - {{ item.shop_name }}</v-list-item-subtitle>
                            </v-list-item-content>
                        </template>
                    </v-autocomplete>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="showMergeModal = false">{{ "Cancel" | t }}</v-btn>
                    <v-btn
                        color="bal-red"
                        dark
                        :disabled="!selectedParentClient"
                        :loading="merging"
                        @click="handleMerge"
                    >
                        {{ "Merge" | t }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <div v-if="activeClient && activeClient.id">
            <client-info-tab />
            <v-divider class="my-2 mx-5" />
            <div class="section-header">{{ "Codes" | t }}</div>
            <client-codes-tab />
            <v-divider class="my-2 mx-5" />
            <div class="section-header">
                {{ "Images" | t }}
                <v-chip
                    class="ml-2"
                    outlined
                    x-small
                    color="#f41823"
                    v-if="activeClient.images && activeClient.images.length > 0"
                >
                    {{ activeClient.images.length }}
                </v-chip>
            </div>
            <image-tab />
        </div>
        <div v-else class="pa-5 text-center">
            <v-progress-circular indeterminate color="primary" />
        </div>
    </v-card>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import ClientInfoTab from "../clients/clients-tab-items/client-info-tab";
import Store from "../../store";
import ImageTab from "@/pages/clients/clients-tab-items/image-tab";
import ClientCodesTab from "@/pages/clients/clients-tab-items/client-codes-tab";
import { PermissionsConstants } from "@/permissions";
import axios from "axios";
import settings from "@/settings";

export default {
    name: "client-view",
    components: {
        ClientCodesTab,
        ImageTab,
        ClientInfoTab
    },
    data() {
        return {
            showMergeModal: false,
            selectedParentClient: null,
            availableClients: [],
            loadingClients: false,
            clientSearch: "",
            merging: false
        };
    },
    mounted() {
        document.addEventListener("keydown", this.handleKeyboard);
        // Load client data if route has clientId and activeClient is empty
        if (this.$route.params.clientId && (!this.activeClient || !this.activeClient.id)) {
            Store.dispatch("ClientStore/getClientData", this.$route.params.clientId);
        }
    },
    destroyed() {
        document.removeEventListener("keydown", this.handleKeyboard);
    },
    watch: {
        clientSearch(val) {
            if (val && val.length >= 2) {
                this.searchClients(val);
            }
        },
        showMergeModal(val) {
            if (!val) {
                this.selectedParentClient = null;
                this.clientSearch = "";
                this.availableClients = [];
            }
        },
    },
    computed: {
        ...mapState("ClientStore", ["activeClient", "loadingClientData"]),
        ...mapGetters(["hasPermissionForAction"]),
        PermissionsConstants() {
            return PermissionsConstants;
        }
    },
    methods: {
        async searchClients(search) {
            this.loadingClients = true;
            try {
                let response = await axios.get(settings.api_url + "/clients/", {
                    params: { search, itemsPerPage: 20 }
                });
                if (response.data.success === 1) {
                    // Filter out the current client
                    this.availableClients = response.data.clients.filter(
                        c => c.id !== this.activeClient.id
                    );
                }
            } catch (e) {
                console.log(e);
            }
            this.loadingClients = false;
        },
        async handleMerge() {
            if (!this.selectedParentClient) return;

            const confirmMsg = this.t("Are you sure you want to merge this client into") +
                ` "${this.selectedParentClient.name}"? ` +
                this.t("This action cannot be undone.");

            if (!confirm(confirmMsg)) return;

            this.merging = true;
            const result = await Store.dispatch("ClientStore/mergeClient", {
                child_id: this.activeClient.id,
                parent_id: this.selectedParentClient.id
            });

            this.merging = false;
            this.showMergeModal = false;

            if (!result.success) {
                alert(this.t("Merge failed") + ": " + (result.error || "Unknown error"));
            }
        },
        closeDrawer() {
            Store.commit("ClientStore/setInfoPanelOpen", false);
            this.$router.push({ name: "client-list" });
        },
        editUser() {
            this.$router.push({
                name: "client-edit",
                params: { clientId: this.$route.params.clientId || this.activeClient.id }
            });
        },
        handleKeyboard(e) {
            if (e.keyCode === 69 && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                this.editUser();
            }
        },
        handleDeleteClick() {
            if (confirm("Are you sure you want to delete this client?")) {
                Store.dispatch("ClientStore/deleteClient", {
                    client_id: this.activeClient.id
                });
            }
        }
    }
};
</script>

<style scoped>
.toolbar-titles {
    display: flex;
    flex-direction: column;
    line-height: 1.2;
}

.toolbar-subtitle {
    font-size: 12px;
    color: #666;
    font-weight: normal;
}

.section-header {
    font-weight: 600;
    font-size: 13px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 4px;
    padding-left: 20px;
    color: #666;
}
</style>
